import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Form, Row, Radio, Tabs, Space } from 'antd';
import {
  MyButton,
  MyForm,
  MyInput,
  MyTabs,
  NumberInput,
  TextError,
} from 'components/Atoms';

import cn from 'classnames';
import { isNumber } from 'lodash';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  BALANCE_MODAL_TYPE,
  DEFAULT_BALANCE_PAYMENT_METHOD_OPTIONS,
  PAYMENT_INTENT_TYPE,
  PAYMENT_TYPE,
  TRANSACTION_TYPE,
} from 'constants/common';
import {
  PAYMENT_TYPE as TOPUP_PAYMENT_TYPE,
  WALLET_TABS,
} from 'constants/common';
import { WALLET } from 'routers/route-path';
import { debounced } from 'utilities/common';
import { showModalError, showModalSuccessfulScreen } from 'utilities/modal';
import {
  calculateTotalDiscountOnePercent,
  calculateTotalCreditSurcharge,
} from 'utilities/numberHelper';
import PaymentLogos from 'components/Pages/Wallet/components/PaymentLogos';
import { selectTransactionQuery } from 'states/transaction/selectors';
import useProfile from 'hooks/useProfile';
import {
  useGetTransactions,
  useTrackingAuditPaymentMethod,
} from 'hooks/usePayment';
import { useGetListPaymentMethod } from 'hooks/usePayment';
import useCreatePaymentMethod from 'hooks/usePaymentMethod';
import useGetCodeValue from 'hooks/discountCode/useGetCodeValue';
import { useCreateTransaction } from '../../../../service';
import BalanceModal from 'components/Organisms/BalanceModal';
import AppliedCodeSection from 'components/Organisms/AppliedDiscountCode';
import { usePaymentTopUp } from 'components/Pages/Wallet/components/WalletTransaction/service';
import TermAndPolicy from 'components/Atoms/TermAndPolicy';
import { formatNumber } from 'utilities/stringHelper';
import { CreditCardIcon } from 'components/Atoms/Icon';
import { PlusOutlined } from '@ant-design/icons';
import { t } from 'utilities/lang';
const { TabPane } = Tabs;
const limitAddFund = 100;
const limitAddFundWireTransfer = 1;

const validation = {
  amount: [
    ({ getFieldValue }) => ({
      validator(_, value) {
        const voucherCodeValue = getFieldValue('voucherCode');
        const paymentMethod = getFieldValue('paymentMethod');

        if (voucherCodeValue) {
          return Promise.resolve();
        }

        if (!voucherCodeValue && !value) {
          return Promise.reject(
            <FormattedMessage id="wallet_topup.error.enter_amount" />
          );
        }

        const isWireTransferMethod =
          paymentMethod === PAYMENT_TYPE.WIRE_TRANSFER;

        const amount = isNumber(value)
          ? value
          : Number(value?.replace(/,/g, ''));

        if (amount && amount < limitAddFund && !isWireTransferMethod) {
          return Promise.reject(
            <FormattedMessage
              id="wallet_topup.error.amount_min"
              values={{ min: limitAddFund }}
            />
          );
        }

        if (amount < limitAddFundWireTransfer && isWireTransferMethod) {
          return Promise.reject(
            <FormattedMessage id="wallet_topup.error.amount_min_wire" />
          );
        }

        return Promise.resolve();
      },
    }),
  ],
  paymentMethod: [
    ({ getFieldValue }) => ({
      validator(_, value) {
        const voucherCodeValue = getFieldValue('voucherCode');

        if (!voucherCodeValue && !value) {
          return Promise.reject(
            <FormattedMessage id="wallet_topup.error.select_payment_method" />
          );
        }

        return Promise.resolve();
      },
    }),
  ],
};

const amounts = [100, 200, 500, 750, 1000, 2000, 3000, 4000];

const paymentMethods = [
  {
    id: TOPUP_PAYMENT_TYPE.CREDIT_CARD,
    text: <FormattedMessage id="wallet_topup.payment_method.credit_card" />,
    subText: <FormattedMessage id="wallet_topup.payment_method.credit_card_sub" />,
    icon: <CreditCardIcon />,
    isDefault: false,
  },
  {
    id: TOPUP_PAYMENT_TYPE.BANK,
    text: <FormattedMessage id="wallet_topup.payment_method.ach_bank_debit" />,
    icon: <CreditCardIcon />,
    isDefault: false,
  },
  {
    id: TOPUP_PAYMENT_TYPE.WIRE_TRANSFER,
    text: <FormattedMessage id="wallet_topup.payment_method.wire_transfer" />,
    subText: <FormattedMessage id="wallet_topup.payment_method.wire_transfer_sub" />,
    icon: <CreditCardIcon />,
    isDefault: false,
  },
];

const FormTopUp = ({
  form,
  onClose,
  loading,
  setLoading,
  checkout,
  listPreviewCards,
}) => {
  const history = useHistory();
  const intl = useIntl(); 
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(undefined);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(WALLET_TABS.TOP_UP);
  const voucherCodeRef = useRef(null);

  const {
    data: voucherData,
    handleChangeParams,
    error: codeError,
  } = useGetCodeValue({
    code: voucherCodeRef.current?.state.value || '',
    paymentIntentType: PAYMENT_INTENT_TYPE.TOP_UP,
  });

  const { data: userInfo } = useProfile();

  const resetSelectedAmount = useCallback(() => {
    setSelectedAmount(undefined);
  }, []);

  const onClickBtnAmount = (value) => () => {
    if (loading) return;
    setSelectedAmount(value);
    form.setFieldsValue({
      amount: value,
    });
  };

  const formatPaymentType = (type) => {
    switch (type) {
      case 'bank':
        return 'achAccount';
      case 'creditCard':
        return 'card';
      default:
        return type;
    }
  };

  const { refetch: refetchListPreviewCards } = useGetListPaymentMethod();
  const trackingPayment = useTrackingAuditPaymentMethod();

  const { onOpenModal: onOpenPaymentMethodModal } = useCreatePaymentMethod(
    `#${BALANCE_MODAL_TYPE.BALANCE_SDK}`,
    {
      ...DEFAULT_BALANCE_PAYMENT_METHOD_OPTIONS,
      onSuccess: (result) => {
        trackingPayment.mutate({
          email: userInfo.adminEmail,
          paymentMethod: formatPaymentType(result.paymentMethodType),
          last4: null,
          paymentMethodId: result.paymentMethodId,
          isDeleted: false,
        });
        console.log('completed with success!');
      },

      onError: (error) => {
        showModalError({ content: error });
      },
      onClose: () => {
        refetchListPreviewCards();
        setPaymentMethodModalOpen(false);
      },
      onOpen: () => {
        setPaymentMethodModalOpen(true);
      },
    }
  );

  const createTransaction = useCreateTransaction();
  const callPaymentTopUp = usePaymentTopUp();
  const { refetch: refetchProfile } = useProfile();
  const filters = useSelector(selectTransactionQuery);
  const { refetch: refetchTransactions } = useGetTransactions(filters);

  const onFinish = async (values) => {
    try {
      const voucherValue = voucherData?.value;

      if (activeKey === WALLET_TABS.TOP_UP && !values.amount) {
        return showModalError({
          content: t('wallet_topup.error.no_topup_amount'),
        });
      }

      if (activeKey === WALLET_TABS.VOUCHER && !voucherValue) {
        return;
      }

      if (voucherValue) {
        setLoading(true);
        return callPaymentTopUp.mutate(
          {
            discountCode: values.voucherCode,
          },
          {
            onSuccess: () => {
              showModalSuccessfulScreen({
                title: t('wallet_topup.success.voucher_applied.title'),
                description: t('wallet_topup.success.voucher_applied.desc', { amount: voucherValue }),
                primaryButton: t('wallet_topup.success.ok'),
                onClickButtonPrimary: () => {
                  history.push(WALLET);
                },
              });
              refetchTransactions();
              refetchProfile();
              form.resetFields();
              setLoading(false);
              onClose();
            },
          }
        );
      }

      const paymentType = checkout?.paymentType;
      const amount = isNumber(values.amount)
        ? values.amount
        : Number(values.amount?.replace(/,/g, ''));

      const calculatedAmount =
        paymentType === PAYMENT_TYPE.WIRE_TRANSFER
          ? calculateTotalDiscountOnePercent(amount)
          : paymentType === PAYMENT_TYPE.CREDIT_CARD
          ? calculateTotalCreditSurcharge(amount)
          : amount;

      const data = {
        amount: calculatedAmount,
        type: TRANSACTION_TYPE.TOPUP,
        paymentMethod: paymentType,
      };

      debounced(() => {
        setLoading(true);
        createTransaction.mutate(data, {
          onSuccess: (data) => {
            const { token, id } = data;
            checkout.transactionInfo.current.transactionId = id;
            checkout.transactionInfo.current.amount = amount;
            checkout.onOpenModal(token);
            setLoading(false);
            onClose();
          },
          onError: () => {
            setLoading(false);
          },
        });
      }, 500);
    } catch (err) {
      setLoading(false);
      setErrorMsg(err?.message || '');
    }
  };

  const onChangePaymentType = async (e) => {
    const { value } = e.target;
    checkout?.onChangePaymentType(value);

    form.setFields([
      {
        name: 'amount',
        errors: null,
      },
    ]);
  };

  const handleClickApply = () => {
    handleChangeParams({ code: voucherCodeRef.current?.state.value });
  };

  const handleChangeTab = (key) => {
    setActiveKey(key);
    form.resetFields();

    if (key === WALLET_TABS.TOP_UP) {
      handleChangeParams({ code: '' });
    }
  };
  const renderPaymentMethod = useMemo(() => {
    let newPaymentMethods = [...paymentMethods];
    const checkDefaultCard = listPreviewCards?.find((card) => card.isDefault);

    if (checkDefaultCard) {
      return newPaymentMethods.map((pm) =>
        pm.id === checkDefaultCard.type
          ? { ...pm, isDefault: true }
          : { ...pm, isDefault: false }
      );
    }
    return newPaymentMethods;
  }, [listPreviewCards, paymentMethods]);

  return (
    <>
      <MyForm form={form} onFinish={onFinish}>
        <MyTabs
          activeKey={activeKey}
          onTabClick={(key) => handleChangeTab(key)}
        >
          <TabPane tab={<FormattedMessage id="wallet_topup.tab.topup_amount" />} key={WALLET_TABS.TOP_UP}>
            <div className="topup-box">
              <div className="topup-title">
                <FormattedMessage id="wallet_topup.title" />
              </div>
              <div className="topup-sub">
                <FormattedMessage id="wallet_topup.subtitle" />
              </div>
              <div className="topup-box-content">
                <Form.Item
                  name="amount"
                  className="form-item-without-optional mb-16"
                  rules={validation.amount}
                >
                  <NumberInput
                    placeholder={intl.formatMessage({ id: "wallet_topup.input.placeholder" })}
                    prefix="USD"
                    disabled={loading}
                    onKeyPress={resetSelectedAmount}
                    className="topup-input"
                  />
                </Form.Item>

                <div>
                  <Row gutter={[16, 16]}>
                    {amounts.map((el) => (
                      <Col key={el} span={6}>
                        <MyButton
                          type="outline"
                          className={cn(
                            'w-100p h-48 btn-outline-gray fz-18 primary-color amount-btn modal-amount-btn',
                            { active: selectedAmount === el }
                          )}
                          onClick={onClickBtnAmount(el)}
                        >
                          {formatNumber(el, {
                            maximumFractionDigits: 0,
                          })}
                        </MyButton>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </div>

            <div className="topup-box mb-0">
              <div className="payment-method-header">
                <div className="topup-title mb-0">
                  <FormattedMessage id="wallet_topup.payment_method.title" />
                </div>
                <span
                  className="add-payment-method-link"
                  onClick={onOpenPaymentMethodModal}
                >
                  <PlusOutlined />
                  <FormattedMessage id="wallet_topup.payment_method.add" />
                </span>
              </div>
              <div className="topup-box-content payment-method-list">
                <Radio.Group
                  name="paymentMethod"
                  value={
                    checkout?.paymentType ||
                    paymentMethods.find((pm) => pm.isDefault)?.id
                  }
                  onChange={onChangePaymentType}
                  className="payment-method-radio-group"
                >
                  {renderPaymentMethod.map((pm) => (
                    <div
                      key={pm.id}
                      className={
                        'payment-method-card-v2' +
                        (checkout?.paymentType === pm.id ||
                        (!checkout?.paymentType && pm.isDefault)
                          ? ' selected'
                          : '')
                      }
                    >
                      <Radio value={pm.id} className="custom-radio">
                        <div className="pm-content">
                          {pm.icon}

                          <div className="pm-info">
                            <div className="pm-row">
                              <span className="pm-name">{pm.text}</span>
                              {pm.isDefault && (
                                <span className="pm-default">
                                  <FormattedMessage id="wallet_topup.payment_method.default" />
                                </span>
                              )}
                            </div>
                            <span className="pm-sub-text">{pm.subText}</span>
                          </div>
                        </div>
                      </Radio>
                    </div>
                  ))}
                </Radio.Group>
              </div>
            </div>

            {errorMsg && <TextError text={errorMsg} />}
          </TabPane>
          <TabPane tab={<FormattedMessage id="wallet_topup.tab.voucher_code" />} key={WALLET_TABS.VOUCHER}>
            <Form.Item
              name="voucherCode"
              label={<FormattedMessage id="wallet_topup.input.voucher_code.label" />}
              className="form-item-without-optional"
            >
              <MyInput
                targetRef={voucherCodeRef}
                suffix={
                  <MyButton
                    type="primary"
                    className="light-blue fw-400"
                    onClick={handleClickApply}
                  >
                    <FormattedMessage id="wallet_topup.input.voucher_code.apply" />
                  </MyButton>
                }
                placeholder={intl.formatMessage({ id: "wallet_topup.input.voucher_code.placeholder" })}
                autoComplete="off"
                className="input-suffix-button"
              />
            </Form.Item>

            {/* <Form.Item
              name="voucherCode"
              label="Voucher Code"
              className="form-item-without-optional voucher-code-form-item"
            >
              <MyInput
                targetRef={voucherCodeRef}
                className="voucher-code-input"
                suffix={
                  <MyButton
                    type="primary"
                    className="voucher-apply-btn"
                    onClick={handleClickApply}
                  >
                    Apply
                  </MyButton>
                }
                placeholder="Enter voucher code"
                autoComplete="off"
              />
            </Form.Item> */}
            {voucherData &&
              form.getFieldValue('voucherCode') &&
              !codeError && (
                <AppliedCodeSection
                  codeType={voucherData.type}
                  codeValue={voucherData.value}
                />
              )}
            {codeError && <AppliedCodeSection codeError={codeError} />}
          </TabPane>
        </MyTabs>
        <PaymentLogos className="mt-32" />
        <TermAndPolicy className="mt-10" />
      </MyForm>
      <BalanceModal
        showModal={paymentMethodModalOpen}
        type={BALANCE_MODAL_TYPE.BALANCE_SDK}
      />
    </>
  );
};

export default FormTopUp;
